import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navbar, Nav, NavDropdown, Form, InputGroup, Button, Badge, Dropdown, Spinner } from 'react-bootstrap';
import { FaSearch, FaFileAlt, FaHospital, FaUserMd, FaPills, FaClipboardList, FaBars, FaFolder, FaUser, FaChevronDown, FaClock, FaUserCheck } from 'react-icons/fa';
import MobileNavigationDrawer from './MobileNavigationDrawer';
import patientService from '../../services/patientService';
import { useSelector, useDispatch } from 'react-redux';
import { addRecentSearch } from '../../redux/slices/navigationSlice';
import './TopbarNavigation.css';

const TopbarNavigation = ({ 
    folders = [], 
    forms = [], 
    onFolderSelect, 
    onFormSelect, 
    onSearchChange, 
    searchTerm = '',
    selectedFolder = 'all',
    patient = null,
    onPatientChange = () => {},
    availablePatients: propAvailablePatients = [] // Legacy prop, now using internal state
}) => {
    const [showSearch, setShowSearch] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [patientSearchTerm, setPatientSearchTerm] = useState('');
    const [showMobileDrawer, setShowMobileDrawer] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [availablePatients, setAvailablePatients] = useState([]);
    const [filteredPatients, setFilteredPatients] = useState([]);
    const [loadingPatients, setLoadingPatients] = useState(false);
    const [searchingPatients, setSearchingPatients] = useState(false);
    const [patientError, setPatientError] = useState(null);
    const [recentPatients, setRecentPatients] = useState([]);
    const searchInputRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const dispatch = useDispatch();
    const navigationState = useSelector(state => state.navigation);
    const activePatients = navigationState?.multiPatientContext?.activePatients || [];

    // Fetch patients from backend with caching
    const fetchPatients = useCallback(async (searchQuery = '') => {
        console.log('🔄 TopbarNavigation: fetchPatients called with:', { searchQuery, loadingPatients });
        
        // Prevent multiple simultaneous calls
        if (loadingPatients) {
            console.log('⚠️ TopbarNavigation: Patient loading already in progress, skipping duplicate call');
            return;
        }
        
        setLoadingPatients(true);
        setPatientError(null);
        
        try {
            let response;
            
            if (searchQuery && searchQuery.length >= 2) {
                console.log('🔍 TopbarNavigation: Searching patients with query:', searchQuery);
                // Use search endpoint for queries
                response = await patientService.searchPatients({
                    query: searchQuery,
                    limit: 20,
                    offset: 0
                });
            } else {
                console.log('📋 TopbarNavigation: Loading all patients for navigation');
                // Use regular getPatients for initial load
                response = await patientService.getPatients({
                    limit: 100,
                    offset: 0,
                    status: 'active'
                });
            }
            
            console.log('📥 TopbarNavigation: Patient response received:', { success: response.success, hasData: !!response.data });
            
            if (response.success && response.data) {
                // Handle both paginated and non-paginated responses
                const patients = response.data.patients || response.data.items || response.data;
                setAvailablePatients(Array.isArray(patients) ? patients : []);
                setFilteredPatients(Array.isArray(patients) ? patients : []);
                
                // Extract recent patients (from active patients in Redux)
                const recent = activePatients
                    .filter(p => p.id !== patient?.id)
                    .slice(0, 3);
                setRecentPatients(recent);
                
                console.log('✅ TopbarNavigation: Patients loaded successfully:', { 
                    totalPatients: Array.isArray(patients) ? patients.length : 0,
                    recentPatients: recent.length 
                });
            } else {
                setPatientError('Failed to load patients');
                console.error('❌ TopbarNavigation: Failed to fetch patients:', response.message);
            }
        } catch (error) {
            setPatientError('Error loading patients');
            console.error('❌ TopbarNavigation: Error fetching patients:', error);
        } finally {
            setLoadingPatients(false);
            console.log('🏁 TopbarNavigation: Patient loading completed');
        }
    }, [loadingPatients]); // Remove activePatients and patient from dependencies to prevent unnecessary re-renders
    
    // Debounced search function
    const debouncedSearch = useCallback(async (searchTerm) => {
        if (searchTerm.length < 2) {
            setFilteredPatients(availablePatients);
            setSearchingPatients(false);
            return;
        }
        
        setSearchingPatients(true);
        
        try {
            // First filter locally for quick response
            const localFiltered = availablePatients.filter(p => 
                (p.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (p.mrn_number || p.mrn || p.id || '').toString().includes(searchTerm)
            );
            
            setFilteredPatients(localFiltered);
            
            // Then search on server if local results are limited
            if (localFiltered.length < 5) {
                const response = await patientService.searchPatients({
                    query: searchTerm,
                    limit: 20,
                    offset: 0
                });
                
                if (response.success && response.data) {
                    const serverPatients = response.data.patients || response.data.items || response.data;
                    setFilteredPatients(Array.isArray(serverPatients) ? serverPatients : localFiltered);
                }
            }
        } catch (error) {
            console.error('Error searching patients:', error);
        } finally {
            setSearchingPatients(false);
        }
    }, [availablePatients]); // Remove fetchPatients and dispatch dependencies
    
    // Update recent patients when activePatients changes
    useEffect(() => {
        const recent = activePatients
            .filter(p => p.id !== patient?.id)
            .slice(0, 3);
        setRecentPatients(recent);
    }, [activePatients, patient?.id]);
    
    // Fetch patients on component mount only once
    useEffect(() => {
        // Only fetch if we don't have patients loaded yet
        if (availablePatients.length === 0 && !loadingPatients) {
            console.log('🚀 TopbarNavigation: Fetching patients for navigation');
            fetchPatients();
        }
    }, []); // Empty dependency array to run only once on mount
    
    // Debounced patient search
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            debouncedSearch(patientSearchTerm);
        }, 300);
        
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [patientSearchTerm, debouncedSearch]);

    // Detect mobile screen size
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 992);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Get parent folders (folders with no parent_folder_id)
    const getParentFolders = () => {
        return folders.filter(folder => !folder.parent_folder_id).map((folder, index) => ({
            id: folder.folder_id,
            name: folder.name,
            description: folder.description,
            icon: getFolderIcon(folder.name),
            color: getFolderColor(folder.name),
            shortcut: (index + 1).toString(),
            originalFolder: folder
        }));
    };

    // Get folder icon based on name
    const getFolderIcon = (folderName) => {
        const name = folderName.toLowerCase();
        if (name.includes('assessment') || name.includes('admission')) return FaHospital;
        if (name.includes('order') || name.includes('treatment') || name.includes('procedure')) return FaPills;
        if (name.includes('documentation') || name.includes('daily') || name.includes('note')) return FaClipboardList;
        if (name.includes('monitoring') || name.includes('vital') || name.includes('track')) return FaUserMd;
        if (name.includes('administrative') || name.includes('insurance') || name.includes('discharge')) return FaFileAlt;
        return FaClipboardList;
    };

    // Get folder color based on name
    const getFolderColor = (folderName) => {
        const name = folderName.toLowerCase();
        if (name.includes('assessment') || name.includes('admission')) return 'success';
        if (name.includes('order') || name.includes('treatment') || name.includes('procedure')) return 'warning';
        if (name.includes('documentation') || name.includes('daily') || name.includes('note')) return 'info';
        if (name.includes('monitoring') || name.includes('vital') || name.includes('track')) return 'primary';
        if (name.includes('administrative') || name.includes('insurance') || name.includes('discharge')) return 'secondary';
        return 'primary';
    };

    const parentFolders = getParentFolders();

    // Get all forms in parent folder + its subfolders
    const getFormsInFolderHierarchy = (parentFolderId) => {
        if (parentFolderId === 'all') return forms;
        
        // Get all subfolders of this parent folder
        const subfolders = folders.filter(f => f.parent_folder_id === parentFolderId);
        const allFolderIds = [parentFolderId, ...subfolders.map(f => f.folder_id)];
        
        // Return forms from parent folder and all its subfolders
        return forms.filter(form => allFolderIds.includes(form.folder_id));
    };

    // Get the parent folder info for a given folder ID
    const getParentFolderInfo = (folderId) => {
        if (folderId === 'all') return { id: 'all', name: 'All Forms', icon: FaClipboardList, color: 'primary' };
        
        const folder = folders.find(f => f.folder_id === folderId);
        if (!folder) return parentFolders[0] || { id: 'all', name: 'All Forms', icon: FaClipboardList, color: 'primary' };

        // If this is already a parent folder, return its info
        const parentFolder = parentFolders.find(pf => pf.id === folderId);
        if (parentFolder) return parentFolder;

        // If this is a subfolder, find its parent
        if (folder.parent_folder_id) {
            return parentFolders.find(pf => pf.id === folder.parent_folder_id) || parentFolders[0];
        }
        
        return parentFolders[0] || { id: 'all', name: 'All Forms', icon: FaClipboardList, color: 'primary' };
    };

    // Organize forms within a folder hierarchy for display
    const organizeFormsInHierarchy = (parentFolderId) => {
        const hierarchyForms = getFormsInFolderHierarchy(parentFolderId);
        const subfolders = folders.filter(f => f.parent_folder_id === parentFolderId);
        
        // Group forms by their immediate folder
        const organized = {
            parentForms: hierarchyForms.filter(form => form.folder_id === parentFolderId),
            subfolderGroups: subfolders.map(subfolder => ({
                folder: subfolder,
                forms: hierarchyForms.filter(form => form.folder_id === subfolder.folder_id)
            })).filter(group => group.forms.length > 0)
        };
        
        return organized;
    };

    // Handle search functionality
    useEffect(() => {
        if (searchTerm.length > 0) {
            const results = forms.filter(form =>
                (form.name?.toLowerCase() || form.formName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                form.description?.toLowerCase().includes(searchTerm.toLowerCase())
            ).slice(0, 8); // Limit to 8 results
            setSearchResults(results);
        } else {
            setSearchResults([]);
        }
    }, [searchTerm, forms]);

    // Basic keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Ctrl + F for search
            if (event.ctrlKey && event.key === 'f') {
                event.preventDefault();
                setShowSearch(true);
                setTimeout(() => searchInputRef.current?.focus(), 100);
            }
            
            // Escape to close search
            if (event.key === 'Escape' && showSearch) {
                setShowSearch(false);
                onSearchChange('');
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showSearch, onSearchChange]);

    // Focus search input when shown
    useEffect(() => {
        if (showSearch && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [showSearch]);

    const handleFormSelect = (form) => {
        onFormSelect(form);
        setShowSearch(false);
        onSearchChange('');
    };

    const handleSearchToggle = () => {
        setShowSearch(!showSearch);
        if (!showSearch) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        } else {
            onSearchChange('');
        }
    };

    return (
        <div className="topbar-navigation">
            <Navbar expand="lg" className="px-3 py-2 border-bottom">
                <div className="d-flex align-items-center justify-content-between w-100">
                    {/* Mobile Menu Button */}
                    {isMobile && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setShowMobileDrawer(true)}
                            className="mobile-menu-btn me-3"
                        >
                            <FaBars />
                        </Button>
                    )}

                    {/* Enhanced Folder Navigation - Hidden on Mobile */}
                    <Nav className={`clinical-folders-nav ${isMobile ? 'd-none' : ''}`}>
                        {parentFolders.map((folder) => {
                            const organizedForms = organizeFormsInHierarchy(folder.id);
                            const totalForms = getFormsInFolderHierarchy(folder.id);
                            const IconComponent = folder.icon;
                            const isActive = selectedFolder === folder.id || 
                                            (selectedFolder !== 'all' && getParentFolderInfo(selectedFolder).id === folder.id);
                            
                            return (
                                <NavDropdown
                                    key={folder.id}
                                    title={
                                        <span className={`nav-folder-title ${isActive ? 'active' : ''}`}>
                                            <IconComponent className={`me-2 text-${folder.color}`} />
                                            {folder.name}
                                        </span>
                                    }
                                    id={`nav-dropdown-${folder.id}`}
                                    className="enhanced-folder-dropdown"
                                >
                                    <div className="dropdown-header d-flex justify-content-between align-items-center">
                                        <span>{folder.name}</span>
                                        <small className="text-muted">{totalForms.length} forms</small>
                                    </div>
                                    <NavDropdown.Divider />
                                    
                                    {/* Direct forms in parent folder */}
                                    {organizedForms.parentForms.length > 0 && (
                                        <>
                                            {organizedForms.parentForms.slice(0, 8).map((form, index) => (
                                                <NavDropdown.Item
                                                    key={`parent-${index}`}
                                                    onClick={() => handleFormSelect(form)}
                                                    className="form-dropdown-item"
                                                >
                                                    <div>
                                                        <div className="form-name">{form.name || form.formName}</div>
                                                        {form.description && (
                                                            <small className="text-muted form-description">
                                                                {form.description.substring(0, 60)}...
                                                            </small>
                                                        )}
                                                    </div>
                                                </NavDropdown.Item>
                                            ))}
                                            {organizedForms.subfolderGroups.length > 0 && <NavDropdown.Divider />}
                                        </>
                                    )}
                                    
                                    {/* Forms from subfolders */}
                                    {organizedForms.subfolderGroups.map((group, groupIndex) => (
                                        <div key={`group-${groupIndex}`}>
                                            <div className="dropdown-header">
                                                <small className="text-primary fw-bold">
                                                    <FaFolder className="me-1" />
                                                    {group.folder.name}
                                                </small>
                                            </div>
                                            {group.forms.slice(0, 5).map((form, formIndex) => (
                                                <NavDropdown.Item
                                                    key={`sub-${groupIndex}-${formIndex}`}
                                                    onClick={() => handleFormSelect(form)}
                                                    className="form-dropdown-item subfolder-form"
                                                >
                                                    <div>
                                                        <div className="form-name">{form.name || form.formName}</div>
                                                        {form.description && (
                                                            <small className="text-muted form-description">
                                                                {form.description.substring(0, 50)}...
                                                            </small>
                                                        )}
                                                    </div>
                                                </NavDropdown.Item>
                                            ))}
                                            {group.forms.length > 5 && (
                                                <NavDropdown.Item className="text-center">
                                                    <small className="text-info">
                                                        +{group.forms.length - 5} more in {group.folder.name}
                                                    </small>
                                                </NavDropdown.Item>
                                            )}
                                            {groupIndex < organizedForms.subfolderGroups.length - 1 && <NavDropdown.Divider />}
                                        </div>
                                    ))}
                                    
                                    {totalForms.length === 0 && (
                                        <NavDropdown.Item disabled>
                                            No forms in this folder
                                        </NavDropdown.Item>
                                    )}
                                    
                                    {totalForms.length > 15 && (
                                        <>
                                            <NavDropdown.Divider />
                                            <NavDropdown.Item 
                                                onClick={() => onFolderSelect(folder.id)}
                                                className="text-center"
                                            >
                                                <small className="text-primary fw-bold">
                                                    View all {totalForms.length} forms in {folder.name}
                                                </small>
                                            </NavDropdown.Item>
                                        </>
                                    )}
                                </NavDropdown>
                            );
                        })}
                    </Nav>

                    {/* Right side: Search only */}
                    <div className="d-flex align-items-center">
                        {/* Search */}
                        <div className="search-container">
                            {showSearch ? (
                                <div className="search-expanded">
                                    <InputGroup>
                                        <Form.Control
                                            ref={searchInputRef}
                                            type="text"
                                            placeholder="Search forms..."
                                            value={searchTerm}
                                            onChange={(e) => onSearchChange(e.target.value)}
                                            className="search-input"
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleSearchToggle}
                                        >
                                            ×
                                        </Button>
                                    </InputGroup>
                                    
                                    {/* Search Results Dropdown */}
                                    {searchResults.length > 0 && (
                                        <div className="search-results">
                                            {searchResults.map((form, index) => (
                                                <div
                                                    key={index}
                                                    className="search-result-item"
                                                    onClick={() => handleFormSelect(form)}
                                                >
                                                    <div className="form-name">{form.name || form.formName}</div>
                                                    {form.description && (
                                                        <small className="text-muted">
                                                            {form.description.substring(0, 80)}...
                                                        </small>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleSearchToggle}
                                    className="search-toggle-btn"
                                >
                                    <FaSearch className="me-1" />
                                    Search
                                </Button>
                            )}
                        </div>

                    </div>
                </div>
            </Navbar>

            {/* Mobile Navigation Drawer */}
            <MobileNavigationDrawer
                show={showMobileDrawer}
                onHide={() => setShowMobileDrawer(false)}
                folders={folders}
                forms={forms}
                onFolderSelect={onFolderSelect}
                onFormSelect={onFormSelect}
                searchTerm={searchTerm}
                onSearchChange={onSearchChange}
                // recentForms={recentForms}
                patient={patient}
                selectedFolder={selectedFolder}
            />

            {/* Patient Context Bar with Patient Switching */}
            {patient && (
                <div className="patient-context-bar px-3 py-2 d-none">
                    <div className="d-flex justify-content-between align-items-center">
                        {/* Patient Info with Switcher */}
                        <div className="patient-info-section">
                            <Dropdown className="patient-switcher-dropdown">
                                <Dropdown.Toggle variant="link" className="patient-switcher-toggle p-0 text-white text-decoration-none">
                                    <div className="d-flex align-items-center">
                                        <FaUser className="me-2" />
                                        <div className="patient-details">
                                            <div className="patient-name-main">
                                                {patient.full_name}
                                            </div>
                                            <small className="patient-meta-info">
                                                MRN: {patient.mrn || patient.id}
                                                {patient.age && (
                                                    <span className="ms-2">
                                                        Age: {patient.age}
                                                    </span>
                                                )}
                                            </small>
                                        </div>
                                        <FaChevronDown className="ms-2 switch-indicator" size={10} />
                                    </div>
                                </Dropdown.Toggle>
                                
                                <Dropdown.Menu className="patient-search-dropdown">
                                    <div className="patient-search-header">
                                        <Form.Control
                                            type="text"
                                            placeholder="Search patients by name or MRN..."
                                            value={patientSearchTerm}
                                            onChange={(e) => setPatientSearchTerm(e.target.value)}
                                            className="patient-search-input"
                                            autoFocus
                                        />
                                    </div>
                                    
                                    {/* Loading State */}
                                    {loadingPatients && (
                                        <Dropdown.Item disabled className="no-patients-message">
                                            Loading patients...
                                        </Dropdown.Item>
                                    )}
                                    
                                    {/* Error State */}
                                    {patientError && (
                                        <>
                                            <Dropdown.Item disabled className="no-patients-message text-danger">
                                                {patientError}
                                            </Dropdown.Item>
                                            <Dropdown.Item 
                                                onClick={fetchPatients}
                                                className="text-center text-primary"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <small>Click to retry</small>
                                            </Dropdown.Item>
                                        </>
                                    )}
                                    
                                    {/* Recent Patients */}
                                    {!patientSearchTerm && recentPatients.length > 0 && (
                                        <>
                                            <div className="dropdown-header d-flex align-items-center">
                                                <FaClock className="me-1 text-muted" size={12} />
                                                <small className="text-muted">Recent Patients</small>
                                            </div>
                                            {recentPatients.map((recentPatient) => (
                                                <Dropdown.Item 
                                                    key={`recent-${recentPatient.id}`}
                                                    onClick={() => {
                                                        onPatientChange(recentPatient, true);
                                                        setPatientSearchTerm('');
                                                    }}
                                                    className="patient-option recent-patient"
                                                >
                                                    <div className="patient-simple-card">
                                                        <div className="d-flex align-items-center">
                                                            <FaUserCheck className="me-2 text-success" size={12} />
                                                            <div>
                                                                <div className="patient-name">
                                                                    {recentPatient.full_name}
                                                                </div>
                                                                <div className="patient-mrn">
                                                                    {recentPatient.mrn || recentPatient.id}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Dropdown.Item>
                                            ))}
                                            <Dropdown.Divider />
                                        </>
                                    )}
                                    
                                    {/* Search Results Loading */}
                                    {searchingPatients && (
                                        <Dropdown.Item disabled className="no-patients-message">
                                            <div className="d-flex align-items-center">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Searching patients...
                                            </div>
                                        </Dropdown.Item>
                                    )}
                                    
                                    {/* Available Patients */}
                                    {!loadingPatients && !patientError && !searchingPatients && filteredPatients.length > 0 ? (
                                        filteredPatients
                                            .filter(p => p.id !== patient?.id)
                                            .slice(0, 8)
                                            .map((availablePatient) => (
                                                <Dropdown.Item 
                                                    key={availablePatient.id}
                                                    onClick={() => {
                                                        onPatientChange(availablePatient, true);
                                                        setPatientSearchTerm('');
                                                    }}
                                                    className="patient-option"
                                                >
                                                    <div className="patient-simple-card">
                                                        <div className="patient-name">
                                                            {availablePatient.full_name || 'Unknown Patient'}
                                                        </div>
                                                        <div className="patient-mrn">
                                                            MRN: {availablePatient.mrn_number || availablePatient.mrn || availablePatient.id}
                                                            {availablePatient.age && (
                                                                <span className="ms-2 text-muted">
                                                                    Age: {availablePatient.age}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {availablePatient.department && (
                                                            <Badge bg="secondary" size="sm" className="mt-1">
                                                                {availablePatient.department}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </Dropdown.Item>
                                            ))
                                    ) : (
                                        !loadingPatients && !patientError && !searchingPatients && filteredPatients.length === 0 && (
                                            <Dropdown.Item disabled className="no-patients-message">
                                                {patientSearchTerm ? `No patients found for "${patientSearchTerm}"` : 'No patients available'}
                                            </Dropdown.Item>
                                        )
                                    )}
                                    
                                    {/* Show more results hint */}
                                    {filteredPatients.length > 8 && (
                                        <>
                                            <Dropdown.Divider />
                                            <Dropdown.Item disabled className="text-center">
                                                <small className="text-muted">
                                                    Showing first 8 of {filteredPatients.filter(p => p.id !== patient?.id).length} results.
                                                    {patientSearchTerm.length < 3 && ' Type more to narrow search.'}
                                                </small>
                                            </Dropdown.Item>
                                        </>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        
                        {/* Patient Status */}
                        <div className="patient-status">
                            <Badge bg="light" text="dark">
                                {patient.care_status?.status || patient.status || 'Active'}
                            </Badge>
                            {patient.department && (
                                <Badge bg="info" className="ms-2">
                                    {patient.department}
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TopbarNavigation;